// Composant réutilisable : recherche dans le plan comptable (hors classe 4).
// Recherche en temps réel par numéro ou libellé, 8 résultats max.
import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Search, Check } from "lucide-react";

export function SelecteurCompte({ value, onSelect, placeholder = "Rechercher un compte (n° ou libellé)…", testId = "selecteur-compte", valeurInitiale = null }) {
  const [terme, setTerme] = useState(() => (valeurInitiale ? `${valeurInitiale.numero} — ${valeurInitiale.libelle || ""}`.trim() : ""));
  const [resultats, setResultats] = useState([]);
  const [ouvert, setOuvert] = useState(false);
  const [indexActif, setIndexActif] = useState(0);
  const conteneurRef = useRef(null);

  // Recherche temps réel (avec petit anti-rebond)
  useEffect(() => {
    const minuteur = setTimeout(async () => {
      try {
        const { data } = await api.get("/plan-comptable", {
          params: { search: terme, exclure_classe_4: true, limit: 8 },
        });
        setResultats(data || []);
        setIndexActif(0);
      } catch {
        setResultats([]);
      }
    }, 200);
    return () => clearTimeout(minuteur);
  }, [terme]);

  // Fermeture au clic extérieur
  useEffect(() => {
    const handler = (e) => {
      if (conteneurRef.current && !conteneurRef.current.contains(e.target)) setOuvert(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const choisir = (compte) => {
    onSelect?.(compte);
    setTerme(`${compte.numero} — ${compte.libelle}`);
    setOuvert(false);
  };

  const surTouche = (e) => {
    if (!ouvert) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setIndexActif((i) => Math.min(i + 1, resultats.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setIndexActif((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (resultats[indexActif]) choisir(resultats[indexActif]);
    } else if (e.key === "Escape") {
      setOuvert(false);
    }
  };

  return (
    <div className="relative" ref={conteneurRef} data-testid={testId}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4A8A8A]" />
        <input
          type="text"
          data-testid={`${testId}-input`}
          value={terme}
          onChange={(e) => { setTerme(e.target.value); setOuvert(true); }}
          onFocus={() => setOuvert(true)}
          onKeyDown={surTouche}
          placeholder={placeholder}
          className="w-full h-10 pl-10 pr-3 rounded-md border border-[#4A8A8A]/30 bg-white text-sm text-[#1D3D3D] focus:outline-none focus:ring-2 focus:ring-[#4A8A8A]/50"
        />
      </div>

      {ouvert && resultats.length > 0 && (
        <div
          data-testid={`${testId}-liste`}
          className="absolute z-50 mt-1 w-full rounded-md border border-[#4A8A8A]/30 bg-white shadow-lg overflow-hidden"
        >
          {resultats.map((c, i) => (
            <button
              key={c.id}
              type="button"
              data-testid={`compte-${c.numero}`}
              onClick={() => choisir(c)}
              onMouseEnter={() => setIndexActif(i)}
              className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 text-sm text-left transition-colors ${
                i === indexActif ? "bg-[#E8F4F4]" : "bg-white hover:bg-[#E8F4F4]/60"
              }`}
            >
              <span className="text-[#1D3D3D]">
                <span className="font-semibold">{c.numero}</span>
                <span className="text-[#1D3D3D]/70"> — {c.libelle}</span>
              </span>
              {value?.numero === c.numero && <Check className="h-4 w-4 text-[#4A8A8A]" />}
            </button>
          ))}
        </div>
      )}

      {ouvert && terme && resultats.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-[#4A8A8A]/30 bg-white shadow-lg px-3 py-3 text-sm text-[#1D3D3D]/60">
          Aucun compte trouvé
        </div>
      )}
    </div>
  );
}

export default SelecteurCompte;
